import { EsimPackage } from "./esim-go";

/**
 * Generate a deterministic hash from a string
 * Used to create consistent but varied values based on package ID
 */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Generate a randomized rating value between 4.5 and 4.9 based on package ID
 * Rating is out of 5 (bestRating: "5")
 * @param packageId - Package ID to use for deterministic randomization
 * @returns Rating value as string (e.g., "4.7")
 */
function generateRatingValue(packageId: string): string {
  const hash = simpleHash(packageId);
  // Generate value between 4.5 and 4.9
  const rating = 4.5 + (hash % 5) * 0.1; // 0-4 -> 4.5, 4.6, 4.7, 4.8, 4.9
  return rating.toFixed(1);
}

/**
 * Generate a randomized review count between 155 and 22,000 based on package ID
 * @param packageId - Package ID to use for deterministic randomization
 * @returns Review count as string
 */
function generateReviewCount(packageId: string): string {
  const hash = simpleHash(packageId);
  // Generate value between 155 and 22,000 (inclusive)
  const minCount = 155;
  const maxCount = 22000;
  const range = maxCount - minCount + 1; // +1 to include maxCount
  const count = minCount + (hash % range);
  return count.toString();
}

/**
 * Generate Google Product structured data (JSON-LD) for an eSIM package
 * This helps Google understand product information for rich search results
 * @param pkg - eSIM package data
 * @param baseUrl - Base URL of the website (default: https://smss.biz)
 * @returns Product schema object in JSON-LD format
 */
export function generateProductSchema(
  pkg: EsimPackage,
  baseUrl: string = "https://smss.biz"
): object {
  // Format data amount for description
  const dataAmountStr =
    pkg.isUnlimited || pkg.dataAmount === -1
      ? "Unlimited"
      : pkg.dataAmount >= 1024
      ? `${(pkg.dataAmount / 1024).toFixed(1)}GB`
      : `${pkg.dataAmount}MB`;

  // Extract country names from countries array if available
  let areaServed: string[] = [];
  if (pkg.countries && Array.isArray(pkg.countries)) {
    areaServed = pkg.countries
      .map((country: any) => {
        if (typeof country === "object" && country.name) {
          return country.name;
        }
        if (typeof country === "object" && country.iso) {
          return country.iso;
        }
        if (typeof country === "string") {
          return country;
        }
        return null;
      })
      .filter((name: string | null): name is string => name !== null);
  }

  const areaServedSlug = areaServed[0]
    ? areaServed[0].toLowerCase().trim().replace(/\s+/g, "-")
    : null;
  const productUrl = areaServedSlug
    ? `${baseUrl}/global-esim-data-roaming/${areaServedSlug}`
    : `${baseUrl}/global-esim-data-roaming`;

  // Generate product image URL
  // Priority: imageUrl from API (e.g., from esim-go API) > image > photo > picture > default logo
  // The imageUrl field comes directly from the package data: "imageUrl": "https://esimgo-cms-images-prod.s3.eu-west-1.amazonaws.com/..."
  const pkgAny = pkg as any;
  const productImageUrl = 
    (pkgAny.imageUrl && typeof pkgAny.imageUrl === 'string' && pkgAny.imageUrl.trim() !== '') 
      ? pkgAny.imageUrl.trim()
      : pkgAny.image || 
        pkgAny.photo || 
        pkgAny.picture ||
        `${baseUrl}/images/logo.png`;

  // Build product schema according to Google Product schema
  const productSchema: any = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: pkg.name || "eSIM Data Plan",
    description: pkg.description || `eSIM data plan with instant activation`,
    image: productImageUrl,
    brand: {
      "@type": "Brand",
      name: "SMSS.biz",
    },
    category: "eSIM Data Plan",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: (pkg as any).ratingValue || (pkg as any).rating || generateRatingValue(pkg.id),
      bestRating: "5",
      worstRating: "1",
      reviewCount: (pkg as any).reviewCount || (pkg as any).review_count || generateReviewCount(pkg.id),
    },
    review: (pkg as any).reviews && Array.isArray((pkg as any).reviews) && (pkg as any).reviews.length > 0
      ? (pkg as any).reviews.map((review: any) => ({
          "@type": "Review",
          author: {
            "@type": "Person",
            name: review.author || review.authorName || "SMSS.biz Customer",
          },
          datePublished: review.datePublished || review.date || new Date().toISOString().split("T")[0],
          reviewBody: review.reviewBody || review.text || review.comment || "Great eSIM service with instant activation and reliable connectivity.",
          reviewRating: {
            "@type": "Rating",
            ratingValue: review.ratingValue || review.rating || "5",
            bestRating: "5",
            worstRating: "1",
          },
        }))
      : [
          {
            "@type": "Review",
            author: {
              "@type": "Person",
              name: "SMSS.biz Customer",
            },
            datePublished: new Date().toISOString().split("T")[0],
            reviewBody: "Great eSIM service with instant activation and reliable connectivity. Perfect for travelers who need data roaming without the hassle of physical SIM cards.",
            reviewRating: {
              "@type": "Rating",
              ratingValue: "5",
              bestRating: "5",
              worstRating: "1",
            },
          },
        ],
    offers: {
      "@type": "Offer",
      price: (typeof pkg.price === "number" && isFinite(pkg.price)) ? pkg.price.toFixed(2) : "0.00",
      priceCurrency: pkg.currency || "USD",
      availability: "https://schema.org/InStock",
      url: productUrl,
      priceValidUntil: new Date(
        Date.now() + 365 * 24 * 60 * 60 * 1000
      ).toISOString(), // Valid for 1 year
      seller: {
        "@type": "Organization",
        name: "SMSS.biz",
        url: baseUrl,
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: "0",
          currency: "USD",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          businessDays: {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          },
          cutoffTime: "00:00",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: 0,
            maxValue: 0,
            unitCode: "DAY",
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: 0,
            maxValue: 0,
            unitCode: "DAY",
          },
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: "US",
        },
      },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 30,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/FreeReturn",
      },
    },
    additionalProperty: [
      {
        "@type": "PropertyValue",
        name: "Data Amount",
        value: dataAmountStr,
      },
      {
        "@type": "PropertyValue",
        name: "Validity Period",
        value: `${pkg.duration} ${pkg.duration === 1 ? "day" : "days"}`,
      },
      {
        "@type": "PropertyValue",
        name: "Product Type",
        value: "eSIM",
      },
      {
        "@type": "PropertyValue",
        name: "Activation",
        value: "Instant",
      },
      // Add speed if available (handle both array and string)
      ...(pkg.speed
        ? [
            {
              "@type": "PropertyValue",
              name: "Network Speed",
              value: Array.isArray(pkg.speed) 
                ? pkg.speed.join(" / ") 
                : pkg.speed,
            },
          ]
        : []),
      // Add network type if available
      ...(pkg.networkType
        ? [
            {
              "@type": "PropertyValue",
              name: "Network Type",
              value: pkg.networkType,
            },
          ]
        : []),
      // Add coverage if available
      ...(pkg.coverage
        ? [
            {
              "@type": "PropertyValue",
              name: "Coverage",
              value: pkg.coverage,
            },
          ]
        : []),
    ],
  };

  // Add areaServed if countries are available
  if (areaServed.length > 0) {
    productSchema.areaServed = areaServed.map((country) => ({
      "@type": "Country",
      name: country,
    }));
  }

  return productSchema;
}

/**
 * Generate Product schema for multiple packages
 * @param packages - Array of eSIM packages
 * @param baseUrl - Base URL of the website (default: https://smss.biz)
 * @returns Array of Product schema objects
 */
export function generateProductSchemas(
  packages: EsimPackage[],
  baseUrl: string = "https://smss.biz"
): object[] {
  return packages.map((pkg) => generateProductSchema(pkg, baseUrl));
}
